import {
  AssetTransactionTypeEnum,
  ProductTypeEnum,
  type AssetTransactionTypeEnum as AssetTransactionTypeEnumType,
  type ProductTypeEnum as ProductTypeEnumType,
} from '@talos/kyoko';

/** Omitted from Adjust Market Account Positions — https://talostrading.atlassian.net/browse/PORT-18150?focusedCommentId=439615 */
export const EXCLUDED_ADJUST_POSITION_TYPES = [
  AssetTransactionTypeEnum.SpotTrade,
  AssetTransactionTypeEnum.TradingFee,
  AssetTransactionTypeEnum.UnrealizedPnL,
  AssetTransactionTypeEnum.ReconAdjustment,
  AssetTransactionTypeEnum.Transfer,
  AssetTransactionTypeEnum.TransferFee,
] as const satisfies readonly AssetTransactionTypeEnumType[];

/** Types that need PositionAsset so P&L is attributed to the correct instrument */
export const POSITION_ASSET_TRANSACTION_TYPES = [
  AssetTransactionTypeEnum.RealizedPnL,
  AssetTransactionTypeEnum.Funding,
] as const satisfies readonly AssetTransactionTypeEnumType[];

const POSITION_ASSET_TRANSACTION_TYPES_SET = new Set<AssetTransactionTypeEnumType>(POSITION_ASSET_TRANSACTION_TYPES);

export function requiresPositionAsset(type: AssetTransactionTypeEnumType | undefined): boolean {
  return type != null && POSITION_ASSET_TRANSACTION_TYPES_SET.has(type);
}

/**
 * Security product types not permitted as Asset/PositionAsset by POST /asset-transactions.
 * Kept in sync with Ava `restrictedAssetTransactionAssetProductTypes` in
 * gocode/portfolio/assettransactionupdatemodel.go. Currencies are always allowed.
 */
export const DENIED_ASSET_TRANSACTION_PRODUCT_TYPES = [
  ProductTypeEnum.Spot,
  ProductTypeEnum.Compound,
  ProductTypeEnum.Synthetic,
] as const satisfies readonly ProductTypeEnumType[];

const DENIED_ASSET_TRANSACTION_PRODUCT_TYPES_SET = new Set<ProductTypeEnumType>(DENIED_ASSET_TRANSACTION_PRODUCT_TYPES);

export function isAllowedAssetTransactionProductType(
  productType: ProductTypeEnumType | undefined
): productType is ProductTypeEnumType {
  return productType != null && !DENIED_ASSET_TRANSACTION_PRODUCT_TYPES_SET.has(productType);
}

const ASSET_TRANSACTION_TYPE_TEXT_MAPPING: Record<AssetTransactionTypeEnumType, string> = {
  [AssetTransactionTypeEnum.Misc]: 'Misc',
  [AssetTransactionTypeEnum.MiscFee]: 'Misc Fee',
  [AssetTransactionTypeEnum.Funding]: 'Funding',
  [AssetTransactionTypeEnum.RealizedPnL]: 'Realized PnL',
  [AssetTransactionTypeEnum.TradingFee]: 'Trading Fee',
  [AssetTransactionTypeEnum.DeliveryFee]: 'Delivery Fee',
  [AssetTransactionTypeEnum.SettlementFee]: 'Settlement Fee',
  [AssetTransactionTypeEnum.Deposit]: 'Deposit',
  [AssetTransactionTypeEnum.Withdrawal]: 'Withdrawal',
  [AssetTransactionTypeEnum.Transfer]: 'Transfer',
  [AssetTransactionTypeEnum.DepositFee]: 'Deposit Fee',
  [AssetTransactionTypeEnum.WithdrawalFee]: 'Withdrawal Fee',
  [AssetTransactionTypeEnum.TransferFee]: 'Transfer Fee',
  [AssetTransactionTypeEnum.SpotTrade]: 'Spot Trade',
  [AssetTransactionTypeEnum.Liquidation]: 'Liquidation',
  [AssetTransactionTypeEnum.LiquidationFee]: 'Liquidation Fee',
  [AssetTransactionTypeEnum.LiquidationProceeds]: 'Liquidation Proceeds',
  [AssetTransactionTypeEnum.UnrealizedPnL]: 'Unrealized PnL',
  [AssetTransactionTypeEnum.Delivery]: 'Delivery',
  [AssetTransactionTypeEnum.ReconAdjustment]: 'Recon Adjustment',
  [AssetTransactionTypeEnum.StakingReward]: 'Staking Reward',
  [AssetTransactionTypeEnum.StakingRewardFee]: 'Staking Reward Fee',
  [AssetTransactionTypeEnum.Settlement]: 'Settlement',
  [AssetTransactionTypeEnum.PreliminaryCreate]: 'Preliminary Create',
  [AssetTransactionTypeEnum.FinalCreate]: 'Final Create',
  [AssetTransactionTypeEnum.PreliminaryRedeem]: 'Preliminary Redeem',
  [AssetTransactionTypeEnum.FinalRedeem]: 'Final Redeem',
  [AssetTransactionTypeEnum.ManagementFee]: 'Management Fee',
  [AssetTransactionTypeEnum.DepositInterest]: 'Deposit Interest',
  [AssetTransactionTypeEnum.LoanPrincipal]: 'Loan Principal',
  [AssetTransactionTypeEnum.LoanPrincipalRepayment]: 'Loan Principal Repayment',
  [AssetTransactionTypeEnum.CollateralPost]: 'Collateral Post',
  [AssetTransactionTypeEnum.CollateralTopUp]: 'Collateral Top Up',
  [AssetTransactionTypeEnum.CollateralWithdrawal]: 'Collateral Withdrawal',
  [AssetTransactionTypeEnum.CollateralRelease]: 'Collateral Release',
  [AssetTransactionTypeEnum.CollateralSubstitutionOut]: 'Collateral Substitution Out',
  [AssetTransactionTypeEnum.CollateralSubstitutionIn]: 'Collateral Substitution In',
  [AssetTransactionTypeEnum.LoanInterest]: 'Loan Interest',
  [AssetTransactionTypeEnum.LateFee]: 'Late Fee',
  [AssetTransactionTypeEnum.PrepaymentFee]: 'Prepayment Fee',
  [AssetTransactionTypeEnum.OriginationFee]: 'Origination Fee',
  [AssetTransactionTypeEnum.CashMarginPayment]: 'Cash Margin Payment',
  [AssetTransactionTypeEnum.ManufacturedPayment]: 'Manufactured Payment',
  [AssetTransactionTypeEnum.ExternalReconAdjustment]: 'External Recon Adjustment',
  [AssetTransactionTypeEnum.InKindCreate]: 'In Kind Create',
  [AssetTransactionTypeEnum.InKindRedeem]: 'In Kind Redeem',
};

export function prettifyAssetTransactionTypeEnum(type: AssetTransactionTypeEnumType | string): string {
  return ASSET_TRANSACTION_TYPE_TEXT_MAPPING[type as AssetTransactionTypeEnumType] ?? type;
}
